/** What this company disclosed before it used EmitIQ, beside what EmitIQ computes — **CTX-04**.
 *
 * ADR-0032 option 2. Every real customer arrives with history, SECR **para 20H** (U-17, read at
 * source 2026-10-04) requires the preceding year's information *"as disclosed in the report for the
 * preceding financial year"*, and that figure was produced by somebody else's method with somebody
 * else's factors. `CLAUDE.md` rule 3 says a figure without a receipt is not shown — so rule 3 is
 * **extended with a second, clearly-labelled species of receipt** rather than weakened.
 *
 * **The backend for all of this was built, tested and unreachable.** `imported.record` had no caller
 * anywhere in the API, nothing served `imported.receipt`, and the verdict could name a difference no
 * screen could show — so *"unresolved differences block approval"* was true of the verdict and
 * invisible to the person who had to act on it. This screen is the reach.
 *
 * Four things it must not do, each of which would be easy and wrong:
 *
 * - **Render an imported receipt as an ordinary one with fields missing.** It is not a calculated
 *   figure and the receipt drawer cannot show it: `ImportedReceipt` has no factor, no engine version
 *   and no activity record, and the provenance sentence says so in words. So the receipt renders
 *   here, labelled, rather than going through the drawer the rest of the product uses.
 * - **Present a difference as an error.** A figure recomputed by a different method legitimately
 *   differs. The product's job is to make somebody account for it, never to decide which number is
 *   right — and a resolution **explains and never corrects**, which the panel says out loud.
 * - **Re-render the figure we compute.** Our side of every difference comes from the report, and the
 *   link goes to the Reports screen rather than to a second rendering of the same arithmetic. Two
 *   copies of a number eventually disagree, and the second is the one nobody updates.
 * - **Let an explained difference look settled when the numbers have moved.** A resolution carries
 *   the two values it was about, so a correction makes it unresolved again — the panel shows the
 *   values the explanation was written against, because that is the part a reader would otherwise
 *   have to take on trust.
 */

import { Archive } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import type { Difference, ImportedFigure } from "../api/imported";
import {
  useImportedDifferences,
  useImportedFigures,
  useImportedReceipt,
  useRecordImported,
  useResolveDifference,
} from "../api/imported";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import type { Scope2Method } from "../api/queries";
import { useObligations, useReport } from "../api/queries";
import { ImportedChain } from "../components/ImportedChain";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { InfoPopover } from "../components/ui/InfoPopover";
import { TablePage } from "../components/ui/TablePage";
import { PageHeaderCard } from "../components/ui/PageHeaderCard";
import { DashTiles } from "../components/ui/Tile";
import { NoObligations } from "./filings/NoObligations";
import { PastFilings } from "./filings/PastFilings";
import { Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { instantDate, shortDate, statedQuantity, unitLabel } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import own from "./Imported.module.css";
import { ScreenFrame } from "./ScreenFrame";
import { SystemDisclosure } from "./SystemDisclosure";
import styles from "./Screen.module.css";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";

function cls(...names: (string | undefined | false)[]): string {
  return names.filter(Boolean).join(" ");
}

/** One disclosed figure's receipt — **rule 3's second species**, rendered as one.
 *
 * Expanded in place rather than in the receipt drawer, and that is the design rather than a
 * shortcut. The drawer resolves a *calculation*: a factor with its publisher and edition, an engine
 * version, an activity record. None of those exist here, and handing this to the drawer would
 * produce an ordinary receipt with blanks — which reads as a broken receipt rather than as a
 * different kind of figure. `ImportedReceiptOut` has no field for any of them, so a component that
 * tried would not compile.
 *
 * The expand toggle is local state rather than a URL parameter, which is a **known limitation**
 * stated rather than hidden: a calculated receipt is addressable (`?receipt=`) and this one is not,
 * so an auditor cannot paste a link to one. Adding a parameter means adding it to `validateSearch`,
 * because a whitelist that silently drops one is worse than none.
 */
function ImportedReceiptPanel({ figureId }: { figureId: string }) {
  const reader = useReader();
  const read = useImportedReceipt(figureId);
  return (
    <div className={cls(own.receipt)} data-testid="imported-receipt">
      <Loaded query={read} of="This receipt">
        {(receipt) => (
          <>
            {/* The label is **first**, before any figure, because the one thing a reader must not
                do is read this as a figure EmitIQ produced. */}
            <p className={cls(own.notCalculated)} data-testid="not-calculated">
              Not calculated by EmitIQ. There is no emission factor, no engine version and no
              activity record behind this number, because there was no calculation — it is the
              figure as somebody disclosed it.
            </p>
            <p className={cls(own.provenance)}>{receipt.provenance}</p>
            {/* **Labelled fields, so their absence is checkable.** The sentence above says there
                is no factor, no engine version and no activity record — and a gate that proved
                that by grepping the rendered text for those words could only pass if the sentence
                were deleted. The guarantee is structural, so the browser gate reads the `dt`
                labels in here and asserts none of them names one. */}
            <dl className={cls(own.receiptFacts)} data-testid="imported-receipt-facts">
              <div>
                <dt>Disclosure</dt>
                <dd>{receipt.disclosure_key}</dd>
              </div>
              <div>
                <dt>Period</dt>
                <dd>
                  {shortDate(receipt.period_start)} – {shortDate(receipt.period_end)}
                </dd>
              </div>
              <div>
                <dt>Asserted</dt>
                <dd>{instantDate(receipt.asserted_at, reader)}</dd>
              </div>
              <div>
                <dt>Stated as</dt>
                <dd>
                  {/* `statedQuantity` rather than the raw string: a `NUMERIC(24, 10)` reaches a
                      screen as `1000.0000000000`, which is the stored scale masquerading as
                      precision — the defect that put `4200.0000000000 tCO2e` on a public panel. */}
                  {receipt.value === null
                    ? "a statement in words"
                    : `${statedQuantity(receipt.value)} ${unitLabel(receipt.unit ?? "")}`}
                </dd>
              </div>
            </dl>
            {receipt.narrative ? (
              <p className={cls(own.narrative)}>{receipt.narrative}</p>
            ) : null}
            {/* The same chain a calculated figure's receipt draws (gate 22): the document it was
                asserted from, who asserted it, that nothing was calculated, and the ledger entry. */}
            <ImportedChain receipt={receipt} />
            {receipt.superseded ? (
              /* **A superseded receipt resolves and says so.** Refusing it would break every link
                 somebody saved; rendering it silently would be worse — a figure that looks live
                 when it has been replaced is the one nobody re-checks (ADR-0030). */
              <p className={cls(own.restated)} data-testid="imported-restated">
                This figure has been restated. A later assertion supersedes it, and the figure in
                force is in the list above.
              </p>
            ) : null}
          </>
        )}
      </Loaded>
    </div>
  );
}

/** One row of the list: what was disclosed, for which period, and its receipt one click away. */
function DisclosedRow({ figure }: { figure: ImportedFigure }) {
  const [open, setOpen] = useState(false);
  const receipt = figure.receipt;
  return (
    <li className={cls(own.disclosed)}>
      <div className={cls(own.disclosedHead)}>
        <span className={cls(own.disclosedWhat)}>
          <span className={cls(own.disclosedKey)}>{receipt.disclosure_key}</span>
          <span className={cls(own.disclosedWhen)}>
            {shortDate(receipt.period_start)} – {shortDate(receipt.period_end)}
          </span>
        </span>
        <span className={cls(own.disclosedValue, "num")}>
          {receipt.value === null
            ? "in words"
            : `${statedQuantity(receipt.value)} ${unitLabel(receipt.unit ?? "")}`}
        </span>
      </div>
      <div className={cls(own.disclosedMeta)}>
        {/* **Whether anything compares it, said rather than implied.** `in_force` matches the
            period exactly, so a comparative for an earlier year is held and is not reconciled —
            and a row that looked reconciled when nothing looked at it would be the worst sentence
            on this screen. */}
        <span
          className={cls(
            own.chip,
            figure.reconciles_this_filing ? own.chipOn : own.chipOff,
          )}
          data-testid={
            figure.reconciles_this_filing ? "reconciled-window" : "comparative-only"
          }
        >
          {figure.reconciles_this_filing
            ? "Reconciled against this filing"
            : "A comparative — not compared with this filing's figures"}
        </span>
        <button
          type="button"
          className={cls(own.toggle)}
          aria-expanded={open}
          data-testid="imported-receipt-toggle"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? "Hide" : "Show"} where this came from
        </button>
      </div>
      {open ? <ImportedReceiptPanel figureId={receipt.imported_figure_id} /> : null}
    </li>
  );
}

/** Explain one difference. A reason is **required**, and nothing here changes either figure. */
function ResolveForm({
  difference,
  obligationId,
  method,
}: {
  difference: Difference;
  obligationId: string | undefined;
  method: Scope2Method;
}) {
  const [reason, setReason] = useState("");
  const resolve = useResolveDifference(obligationId, method);
  return (
    <div className={cls(own.resolve)}>
      <label className={cls(own.resolveLabel)} htmlFor={`why-${difference.disclosure_key}`}>
        Why do these differ?
      </label>
      <textarea
        id={`why-${difference.disclosure_key}`}
        rows={2}
        className={cls(own.resolveInput)}
        data-testid="resolve-reason"
        placeholder="A different method, a different boundary, a restatement."
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      <div className={cls(own.resolveRow)}>
        <button
          type="button"
          className={cls(styles.primary)}
          data-testid="resolve-difference"
          // Disabled until there is a reason, because the server requires one and a button that
          // predictably 422s is the same defect as one that predictably 403s.
          disabled={reason.trim().length < 3 || resolve.isPending}
          onClick={() =>
            resolve.mutate(
              {
                figureId: difference.imported_figure_id,
                difference,
                reason,
              },
              { onSuccess: () => setReason("") },
            )
          }
        >
          {resolve.isPending ? "Recording…" : "Record this explanation"}
        </button>
        {/* Cancel, once there is something to cancel: an explanation typed against one difference
            and abandoned must not be waiting on the next one somebody opens — it would read as an
            account of a gap it was never written about. */}
        {reason ? (
          <button
            type="button"
            className={cls(styles.fixLink)}
            data-testid="cancel-resolution"
            onClick={() => {
              setReason("");
              resolve.reset();
            }}
          >
            Cancel
          </button>
        ) : null}
        {resolve.isError ? (
          /* **A resolution carries both values** (CTX-04), so a difference whose numbers have moved
             since this panel opened is refused — and the remedy is to look at what moved, which is
             a reload rather than a re-send of the same explanation. */
          <WriteFailed
            error={resolve.error}
            of="this difference"
            className={cls(own.resolveError)}
          />
        ) : (
          <span className={cls(own.resolveNote)}>
            This explains the difference. It changes neither figure — if ours is wrong, correct the
            activity behind it and this difference will change with it.
          </span>
        )}
      </div>
    </div>
  );
}

/** One difference: both numbers, the gap, and either the explanation or the form for one. */
function DifferenceRow({
  difference,
  obligationId,
  method,
  canResolve,
  search,
}: {
  difference: Difference;
  obligationId: string | undefined;
  method: Scope2Method;
  canResolve: boolean;
  search: ReturnType<typeof keepSearch>;
}) {
  const reader = useReader();
  const explained = difference.resolution;
  return (
    <li
      className={cls(own.difference, explained ? own.explained : own.unexplained)}
      data-testid={explained ? "difference-explained" : "difference-unexplained"}
    >
      <div className={cls(own.differenceHead)}>
        <span className={cls(own.differenceKey)}>{difference.disclosure_key}</span>
        <span
          className={cls(own.differenceState)}
          data-testid={explained ? "explained-chip" : "unexplained-chip"}
        >
          {explained ? "Explained" : "Unexplained — this holds the filing"}
        </span>
      </div>

      <div className={cls(own.sides)}>
        <span className={cls(own.side)}>
          <span className={cls(own.sideLabel)}>Previously disclosed</span>
          <span className={cls(own.sideValue, "num")}>
            {statedQuantity(difference.imported)} {unitLabel(difference.unit)}
          </span>
        </span>
        <span className={cls(own.side)}>
          {/* **Borrowed, never re-rendered.** Our side is the report's own line, and the link goes
              there rather than to a second rendering of the same arithmetic. */}
          <span className={cls(own.sideLabel)}>What EmitIQ computes</span>
          <Link to="/reports" search={search} className={cls(own.sideValue, own.sideLink, "num")}>
            {statedQuantity(difference.ours)} {unitLabel(difference.unit)}
          </Link>
        </span>
        <span className={cls(own.side)}>
          <span className={cls(own.sideLabel)}>Difference</span>
          <span className={cls(own.sideValue, own.delta, "num")} data-testid="difference-delta">
            {signed(difference.delta)} {unitLabel(difference.unit)}
            {difference.percent === null ? null : (
              <span className={cls(own.percent)}>{signed(difference.percent)}%</span>
            )}
          </span>
        </span>
      </div>

      {explained ? (
        <div className={cls(own.explanation)} data-testid="difference-explanation">
          <p className={cls(own.explanationReason)}>{explained.reason}</p>
          {/* **The values the explanation was written against.** On the row rather than taken on
              trust: a difference whose numbers no longer match these is unresolved again,
              automatically, because the explanation was about a gap that no longer exists. Without
              that, a reconciliation signed in March still reads as resolved in June after a
              correction moved our figure. */}
          <p className={cls(own.explanationValues)}>
            Written on {instantDate(explained.resolved_at, reader)} about{" "}
            {statedQuantity(explained.imported_value)} {unitLabel(explained.unit)} against{" "}
            {statedQuantity(explained.our_value)} {unitLabel(explained.unit)}. If either figure moves, this
            difference is unexplained again.
          </p>
        </div>
      ) : canResolve ? (
        <ResolveForm difference={difference} obligationId={obligationId} method={method} />
      ) : (
        <p className={cls(styles.note)}>
          An <strong>Approver</strong> explains a difference against a figure the company has
          already disclosed.
        </p>
      )}
    </li>
  );
}

/** A decimal string with its sign on the front. `-` is already there; `+` is not.
 *
 * The direction is the first thing anybody asks about a difference, and an unsigned one reads as a
 * magnitude. The server already signs `delta`; `percent` it does not, so both go through this and
 * neither is signed twice.
 */
function signed(value: string): string {
  const trimmed = statedQuantity(value.replace(/^\+/, ""));
  return trimmed.startsWith("-") ? trimmed : `+${trimmed}`;
}

/** Record what the company disclosed. Approver only, and the server is the one that enforces it. */
function RecordForm({
  obligationId,
  method,
  filingWindow,
}: {
  obligationId: string | undefined;
  method: Scope2Method;
  //: The **filing's** period, from the report's own context — not the period picker at the top of
  //: the app. A comparative belongs to a stated year, and a group whose subsidiary has a March
  //: year end has two different ones open at once (F11).
  filingWindow: { period_start: string; period_end: string } | undefined;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("tCO2e");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [source, setSource] = useState("");
  const record = useRecordImported(obligationId, method);

  // Prefilled from the filing rather than from today, so the commonest case — reconciling the
  // opening total for the year being filed — needs no typing. It is still editable, because a
  // comparative under 20H is for the *preceding* year and the two are different jobs.
  const start = from || filingWindow?.period_start || "";
  const end = to || filingWindow?.period_end || "";

  return (
    <div className={cls(own.form)} data-testid="record-imported">
      <div className={cls(own.formGrid)}>
        <label className={cls(styles.field)}>
          Disclosure
          <input
            type="text"
            value={key}
            data-testid="imported-disclosure"
            placeholder="secr_gas_combustion"
            onChange={(event) => setKey(event.target.value)}
          />
        </label>
        <label className={cls(styles.field)}>
          Figure
          <input
            type="text"
            inputMode="decimal"
            value={value}
            data-testid="imported-value"
            placeholder="1000"
            onChange={(event) => setValue(event.target.value)}
          />
        </label>
        <label className={cls(styles.field)}>
          Unit
          <input
            type="text"
            value={unit}
            data-testid="imported-unit"
            onChange={(event) => setUnit(event.target.value)}
          />
        </label>
        <label className={cls(styles.field)}>
          Period from
          <input
            type="date"
            value={start}
            data-testid="imported-from"
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className={cls(styles.field)}>
          Period to
          <input
            type="date"
            value={end}
            data-testid="imported-to"
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </div>
      <label className={cls(styles.field, own.wide)}>
        Where it came from
        <input
          type="text"
          value={source}
          data-testid="imported-source"
          placeholder="the 2024 annual report, directors' report page 44"
          onChange={(event) => setSource(event.target.value)}
        />
      </label>
      <div className={cls(own.formRow)}>
        <button
          type="button"
          className={cls(styles.primary)}
          data-testid="record-imported-save"
          disabled={
            record.isPending ||
            !key.trim() ||
            !value.trim() ||
            !unit.trim() ||
            !source.trim() ||
            !start ||
            !end
          }
          onClick={() =>
            record.mutate(
              {
                disclosure_key: key.trim(),
                period_start: start,
                period_end: end,
                value: value.trim(),
                unit: unit.trim(),
                source_description: source.trim(),
              },
              {
                onSuccess: () => {
                  setKey("");
                  setValue("");
                  setSource("");
                },
              },
            )
          }
        >
          {record.isPending ? "Recording…" : "Record this figure"}
        </button>
        {/* Cancel. Every field, including the two the success path leaves — a unit and a period
            left behind are exactly what makes the next figure inherit this one's year. */}
        {key || value || source || from || to ? (
          <button
            type="button"
            className={cls(styles.fixLink)}
            data-testid="cancel-imported"
            onClick={() => {
              setKey("");
              setValue("");
              setUnit("tCO2e");
              setFrom("");
              setTo("");
              setSource("");
              record.reset();
            }}
          >
            Cancel
          </button>
        ) : null}
        {record.isError ? (
          <WriteFailed
            error={record.error}
            of="what was previously disclosed"
            testId="imported-refused"
            className={cls(own.resolveError)}
          />
        ) : (
          <span className={cls(own.resolveNote)}>
            {/* The entity and the regime are **not** on this form, and that is deliberate: the
                server derives both from the filing above (ADR-0029). A form that could choose
                them could hold a comparative against a company this filing is not about. */}
            Held against the filing selected above. It is never added to this year&rsquo;s
            inventory, and every assertion is recorded in the ledger.
          </span>
        )}
      </div>
    </div>
  );
}

function ImportedScreenBody() {
  const screen = useScreen();
  /** The read, not `data = []`. With no obligation `key` is `""`, every query below is disabled,
   * and the three panels render as a workspace with nothing filed and nothing to reconcile — a
   * claim about the company, produced by one dropped request (EXP-29). */
  const obligationsRead = useObligations();
  const obligations = obligationsRead.data ?? [];
  const { data: me } = useMe();

  // Ordered so the filings that are actually owed come first, like the Reports screen: a default
  // selection of "not in scope" opens on a screen with nothing on it.
  const filings = [...obligations].sort((a, b) => {
    const owed = Number(b.status === "required") - Number(a.status === "required");
    return owed || a.legal_entity_name.localeCompare(b.legal_entity_name);
  });
  /** Which filing, **from the URL** — the same parameter the Reports screen reads (UX-01).
   *
   * One fact, one home. Both screens are about *a filing*, and while each held its own `useState`
   * moving between them changed the subject without saying so: a reader who selected the parent's
   * SECR return on Reports and followed the rail to the reconciliation arrived on whichever
   * obligation sorted first here. It also made the bridge's own link possible — the first-year
   * branch of *Against last year* sends a reader here **for the year it was talking about**, and a
   * link that could not name the filing would have been a link to a screen about something else.
   *
   * A filing that is not on offer is not in force, which is `register.ts`'s rule.
   */
  const asked = screen.filing;
  const offered = asked && filings.some((o) => o.obligation_id === asked) ? asked : "";
  const key = offered || filings[0]?.obligation_id || "";
  /** The regime of the filing in view, **by the name its row carries** - never a regime written into
   * this screen's copy, which every regime shares (whole-product review 2026-11, finding 11). */
  const selectedRegime = filings.find((o) => o.obligation_id === key)?.regime_name ?? "this regime";

  const figuresRead = useImportedFigures(key || undefined);
  const differencesRead = useImportedDifferences(key || undefined, screen.method);
  // The report, for its **verdict** — read rather than re-derived. An unexplained difference holds
  // the filing and `regimes.validation` is what says so; a second sentence computed here could
  // disagree with the one on the Reports screen, and the one people would act on is whichever they
  // happened to be looking at.
  const reportRead = useReport(key || undefined, screen.method);
  // **An Approver's act**, like accepting a supplier's figure: saying *"this is what we filed"* is
  // a statement about the company's own disclosure, not data entry. A Contributor is told whose
  // decision it is rather than shown a control that predictably 403s (UX-04, design rule 6).
  const canResolve = me?.role === "approver" || me?.role === "admin";
  const search = keepSearch({
    method: screen.method,
    // Absent when the workspace has no reporting period, and `keepSearch` leaves the keys off the
    // address rather than carrying a date nobody chose.
    from: screen.window?.period_start,
    to: screen.window?.period_end,
  });

  const unexplained = (differencesRead.data ?? []).filter((row) => row.resolution === null);

  /** **A workspace with no filing obligation has nothing to reconcile, and says so.** With no
   * obligation `key` is `""`, so both queries below are disabled and stay `isPending` for ever —
   * and `Loaded` printed "Loading…" in two panels, indefinitely, over a screen that had finished.
   * Found by `shell/screenStates.test.tsx` driving this screen over an empty workspace. The
   * stand-in is a *settled, empty* read, so the panels use their one empty path rather than a
   * second; the sentence is different because "no figure disagrees" is a claim about a filing, and
   * there is none. */
  const noFiling = !obligationsRead.isPending && !obligationsRead.error && key === "";
  const NO_FILING =
    "This workspace has no filing obligation, so there is nothing to compare a prior disclosure with. Filings appear on Reports once a regime applies.";
  const nothingToRead = {
    data: [] as never[],
    isPending: false,
    error: null,
    refetch: () => undefined,
  };

  // What each read says, **only once it has succeeded.** A failed refresh keeps the last answer in
  // the cache, and a count of it would be a claim about the filing made by a dropped request.
  const differences = differencesRead.error || noFiling ? undefined : differencesRead.data;
  const figures = figuresRead.error || noFiling ? undefined : figuresRead.data;
  const explainedCount = differences ? differences.length - unexplained.length : 0;
  const verdictKnown = Boolean(reportRead.data) && Boolean(differences);

  // **No filing: one designed empty state, and nothing else** (KD, 2026-11-02). Two panels repeating
  // that there is nothing to compare, and a form whose button could only fail, were this page's first
  // look for a new workspace. The honest sentence (`NO_FILING`) is kept where a person can reach it:
  // the info control in the header.
  if (noFiling) {
    return (
      <ScreenFrame screen={screen} title="Prior disclosures" crumb="Past filings" bodyScrolls>
        <TablePage
          icon={Archive}
          title="Filings"
          label="Past filings"
          actions={<InfoPopover infoKey="filings.prior" />}
          tiles={<DashTiles labels={["Filings issued", "Latest issued", "Filings owed"]} label="Past filings summary" />}
          state={<NoObligations title="Nothing filed yet" />}
        />
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame
      screen={screen}
      title="Prior disclosures"
      crumb="What was filed before, beside what EmitIQ computes"
      bodyScrolls
    >
      <PageHeaderCard
        icon={Archive}
        title="Filings"
        label="Past filings"
        actions={<InfoPopover infoKey="filings.prior" />}
      />

      <div className={cls(own.stack)}>
        {/* Past filings (ADR-0212): the filings that were issued, before the reconciliation. */}
        {obligationsRead.data && obligationsRead.data.length > 0 ? (
          <PastFilings filings={obligationsRead.data} />
        ) : null}
        {obligationsRead.error ? (
          <ReadFailed
            of="This workspace's filing obligations"
            error={obligationsRead.error}
            onRetry={() => void obligationsRead.refetch()}
          />
        ) : null}

        {/* Which filing: the same `filing` parameter the Reports screen reads, so moving between
            the two never changes the subject without saying so. */}
        <div className={cls(own.picker)} data-testid="imported-filings">
          {filings.map((filing) => (
            <button
              key={filing.obligation_id}
              type="button"
              className={cls(own.pick, filing.obligation_id === key && own.pickOn)}
              aria-pressed={filing.obligation_id === key}
              onClick={() => screen.setFiling(filing.obligation_id)}
              title={`${filing.legal_entity_name} · ${filing.status.replace(/_/g, " ")}`}
            >
              {filing.regime_key.replace(/_/g, " ")}
              <span className={cls(own.pickEntity)}>{filing.legal_entity_name}</span>
            </button>
          ))}
        </div>

        {/* **Counts only from a read that succeeded**, and no tile at all otherwise. A zero over a
            failed read is a claim that nothing was disclosed (EXP-29). */}
        {figures && differences ? (
          // Three facts about the filing in view, as one line and not a second strip of tiles: the page's
          // strip is the summary of past filings above, and a page holds three to five tiles in all.
          <dl className={cls(own.facts)} data-testid="imported-tiles" aria-label="This filing's prior disclosures">
            <div className={cls(own.fact)} data-kpi="imported-figures">
              <dt>
                Previously disclosed <InfoPopover infoKey="filings.prior-disclosed" />
              </dt>
              <dd className="num">{String(figures.length)}</dd>
            </div>
            <div className={cls(own.fact)} data-kpi="imported-differences">
              <dt>
                Differences found <InfoPopover infoKey="filings.prior-differences" />
              </dt>
              <dd className="num">{String(differences.length)}</dd>
            </div>
            <div className={cls(own.fact)} data-kpi="imported-unexplained">
              <dt>Unexplained</dt>
              <dd className="num">{String(unexplained.length)}</dd>
              <dd className={cls(own.tileNote)}>
                {unexplained.length > 0 ? "These hold the filing" : "Nothing held"}
              </dd>
            </div>
          </dl>
        ) : null}

        {/* **The consequence, where it cannot be missed.** The verdict comes from the report so the
            two screens cannot disagree about whether this filing is ready.

            **And only when both reads succeeded.** This sentence used to render on the report
            alone, with `unexplained` built from `differencesRead.data ?? []` — so a failed
            reconciliation read printed *"Nothing on this filing disagrees unaccountably"*, the one
            sentence this screen must never make by accident. */}
        {reportRead.error ? (
          <ReadFailed
            of="This filing's verdict"
            error={reportRead.error}
            onRetry={() => void reportRead.refetch()}
          />
        ) : null}
        {verdictKnown ? (
          <p
            className={cls(
              own.verdict,
              unexplained.length > 0 ? own.verdictHeld : own.verdictClear,
            )}
            data-testid="imported-verdict"
          >
            <StatusPill tone={unexplained.length > 0 ? "crit" : "ok"}>
              {unexplained.length > 0 ? "Holds the filing" : "Nothing held"}
            </StatusPill>
            {unexplained.length > 0 ? (
              <span>
                <strong>
                  {unexplained.length} unexplained difference
                  {unexplained.length === 1 ? "" : "s"} on this filing.
                </strong>{" "}
                A difference is not an error — a figure produced by a different method legitimately
                differs — but EmitIQ will not file over one silently. Explain each, or correct the
                activity behind it.
              </span>
            ) : (
              <span>
                Nothing on this filing disagrees unaccountably with what was previously disclosed
                {explainedCount > 0 ? ` (${explainedCount} explained)` : ""}. That is not approval:
                whether this report may lawfully be filed is a person&rsquo;s judgement.
              </span>
            )}
          </p>
        ) : null}

        <div className={cls(own.columns)}>
          <Panel
            title="Reconciliation"
            sub="This year's figure beside what was published"
          >
            {/* **A failed read is not "nothing disagrees"** (EXP-29). That sentence is a claim about
                the company, and it is the one this screen must never make by accident. */}
            <Loaded
              query={noFiling ? nothingToRead : differencesRead}
              of="This reconciliation"
              empty={
                noFiling
                  ? NO_FILING
                  : "No figure on this filing disagrees with what was previously disclosed. Record a disclosed figure below to compare one."
              }
            >
              {(rows) => (
                <ul className={cls(own.differences)} data-testid="imported-differences">
                  {rows.map((difference) => (
                    <DifferenceRow
                      key={`${difference.disclosure_key}-${difference.imported_figure_id}`}
                      difference={difference}
                      obligationId={key || undefined}
                      method={screen.method}
                      canResolve={canResolve}
                      search={search}
                    />
                  ))}
                </ul>
              )}
            </Loaded>
          </Panel>

          <Panel
            title="What was disclosed"
            sub="Held as asserted figures, never mixed into this year's inventory"
          >
            <Loaded
              query={noFiling ? nothingToRead : figuresRead}
              of="What was previously disclosed"
              empty={
                noFiling
                  ? NO_FILING
                  : `Nothing has been recorded as previously disclosed for this filing. If ${selectedRegime} asks for the preceding year's figures as the company published them, record them here.`
              }
            >
              {(rows) => (
                <ul className={cls(own.disclosedList)} data-testid="imported-figures">
                  {rows.map((figure) => (
                    <DisclosedRow key={figure.receipt.imported_figure_id} figure={figure} />
                  ))}
                </ul>
              )}
            </Loaded>
          </Panel>
        </div>

        <Panel
          title="Record a disclosed figure"
          sub="What the company published, as it published it"
        >
          {canResolve ? (
            <RecordForm
              obligationId={key || undefined}
              method={screen.method}
              filingWindow={
                reportRead.data
                  ? {
                      period_start: reportRead.data.context.period_start,
                      period_end: reportRead.data.context.period_end,
                    }
                  : undefined
              }
            />
          ) : (
            /* Told whose decision it is, rather than shown a form that predictably 403s. */
            <p className={cls(styles.note)} data-testid="imported-who-can">
              An <strong>Approver</strong> records what the company previously disclosed. It is a
              statement about the company&rsquo;s own filing rather than data entry.
            </p>
          )}
        </Panel>

        <Panel title="Basis and limits" sub="What an asserted figure is, and is not">
          <SystemDisclosure summary="Rule 3, extended rather than weakened" testId="imported-basis" lazy>
            <p>
              An imported figure has no factor, no engine version and no calculation behind it,
              because there was none. It resolves to a receipt of its own — asserted by a person,
              from a named source, on a date — and it is held in its own table so that no sum over
              this year&rsquo;s inventory can reach it.
            </p>
            <p>
              A resolution explains a difference and never corrects it. It carries the two values it
              was written about, so a correction that moves either figure makes the difference
              unexplained again.
            </p>
          </SystemDisclosure>
        </Panel>
      </div>
    </ScreenFrame>
  );
}

/** Wrapped, because every endpoint these panels read answers only to a session. Without it the
 * screen would render its empty state while every query 401s — which looks like a workspace with
 * nothing in it rather than a person who is not signed in. */
export function ImportedScreen() {
  return (
    <RequireSession>
      <ImportedScreenBody />
    </RequireSession>
  );
}
